import { useEffect, useRef } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Markdown } from "../../components/Markdown";
import { Card, PageHeader } from "../../components/ui";
import { docBySlug, headingsOf, sectionOf } from "../../docs";
import { useActiveSection, useRailFollows } from "../../lib/useActiveSection";
import { NotFoundPage } from "../NotFoundPage";
import { LIBRARY } from "./DocsLayout";

/** One document from the library, with "on this page" beside it. */
export function DocPage() {
  const { slug } = useParams();
  const { hash } = useLocation();
  const doc = docBySlug(slug);
  const source = doc ? (doc.section ? sectionOf(doc.markdown, doc.section) : doc.markdown.replace(/^# .*\n/, "")) : "";
  const headings = headingsOf(source);

  useEffect(() => {
    if (!hash) return;
    const target = document.getElementById(hash.slice(1));
    if (!target) return;
    target.scrollIntoView({ block: "start" });
    target.focus({ preventScroll: true });
  }, [hash, slug]);

  if (!doc) return <NotFoundPage />;
  return (
    <>
      <PageHeader title={doc.title} subtitle={doc.summary} />
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_13rem] lg:items-start lg:gap-8">
        <Card>
          <article className="max-w-3xl" data-testid="doc-article">
            <Markdown source={source} />
          </article>
        </Card>
        {headings.length > 1 && <DocRail headings={headings} />}
      </div>
    </>
  );
}

/**
 * "On this page": marks the section being read and follows the scroll both
 * ways (#410). The hook's state lives here, not in DocPage (#417): a change
 * of section re-renders this list alone, never the document beside it.
 */
function DocRail({ headings }: { headings: ReturnType<typeof headingsOf> }) {
  const { hash } = useLocation();
  const active = useActiveSection(
    headings.map((h) => h.id),
    hash,
  );
  const railRef = useRef<HTMLElement>(null);
  useRailFollows(railRef, active);
  return (
    // Under the frozen page header, not behind it.
    <nav ref={railRef} aria-label="On this page" className="mt-6 lg:sticky lg:top-[calc(var(--page-bar)+1rem)] lg:mt-0 lg:max-h-[calc(100vh-var(--page-bar)-1.5rem-var(--docs-below,1rem))] lg:overflow-y-auto">
      <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">On this page</p>
      <ul className="mt-2 text-sm">
        {headings.map((h) => {
          const current = active === h.id;
          return (
            <li key={h.id}>
              {/* A left rule, not a tint, marks the current entry - as on the release-notes rail. */}
              <Link
                to={`#${h.id}`}
                aria-current={current ? "location" : undefined}
                className={`block border-l-2 py-1 text-neutral-700 hover:underline dark:text-neutral-300 ${h.level === 3 ? "pl-5" : "pl-2.5"} ${current ? "border-brand-text font-semibold" : "border-transparent"}`}
              >
                {h.text}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/** `/docs` itself: the shelf. */
export function DocsIndexPage() {
  return (
    <>
      <PageHeader title="Documentation" subtitle="Everything a customer needs to deploy, operate and sign in to Starsach - the same files that ship with your API key, kept current with every release." />
      <ul className="grid gap-4 sm:grid-cols-2">
        {LIBRARY.map((e) => (
          <li key={e.to}>
            <Link to={e.to} className="block h-full rounded-xl border border-neutral-200 bg-white p-4 transition-shadow hover:shadow-md dark:border-neutral-800 dark:bg-neutral-900">
              <span className="block font-semibold">{e.title}</span>
              <span className="mt-1 block text-sm text-neutral-600 dark:text-neutral-400">{e.summary}</span>
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}
